import { useRouter } from "next/router";
import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  AuthService,
  type AuthStatusResponse,
  UnifiedAuthService,
} from "@/services/client/unifiedAuth";

interface AuthContextType {
  // Auth status from the new centralized system
  authStatus: AuthStatusResponse | null;

  // Derived states for backward compatibility
  isAuthenticated: boolean;
  isLoading: boolean;
  org: string | null;
  orgName: string | null;
  jobberAccountId: string | null;

  // Service-specific states
  hasJobberAuth: boolean;
  jobberAuthExpiresAt: string | null;
  needsJobberAuth: boolean;
  hasEagleViewAuth: boolean;
  eagleViewAuthMode: string | null;
  needsEagleViewAuth: boolean;
  isFullyAuthenticated: boolean;

  // Auth actions
  login: (org: string) => void;
  logout: () => void;
  handleOAuthCallback: (token: string, org: string) => Promise<void>;
  refreshToken: () => Promise<boolean>;
  checkAuthStatus: () => Promise<AuthStatusResponse | null>;
  getAuthStatus: () => Promise<AuthStatusResponse | null>;
  ensureValidAuth: () => Promise<boolean>;

  // Backward compatibility
  makeApiRequest: <T = any>(
    endpoint: string,
    options?: RequestInit,
  ) => Promise<{ data?: T; error?: string }>;
  makeInternalRequest: <T = any>(
    endpoint: string,
    options?: RequestInit,
  ) => Promise<{ data?: T; error?: string }>;
  fetchJobberAccountInfo: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authStatus, setAuthStatus] = useState<AuthStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastCheckTime, setLastCheckTime] = useState<number>(0);
  const [org, setOrg] = useState<string | null>(null);
  const [orgName, setOrgName] = useState<string | null>(null);
  const [jobberAccountId, setJobberAccountId] = useState<string | null>(null);

  const router = useRouter();
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCheckingRef = useRef(false);

  // Cache duration in milliseconds (30 seconds for non-critical checks)
  const CACHE_DURATION = 30000;

  // Polling interval (5 minutes)
  const POLL_INTERVAL = 5 * 60 * 1000;

  // Derived states from authStatus
  const isAuthenticated = authStatus?.api_session?.valid || false;
  const hasJobberAuth = authStatus?.jobber?.valid || false;
  const jobberAuthExpiresAt = authStatus?.jobber?.expires_at || null;
  const hasEagleViewAuth = authStatus?.eagleview?.valid || false;
  const eagleViewAuthMode = authStatus?.eagleview?.has_oauth
    ? "oauth"
    : authStatus?.eagleview?.has_api_key
      ? "api_key"
      : null;
  const needsJobberAuth = isAuthenticated && !hasJobberAuth;
  const needsEagleViewAuth =
    isAuthenticated && hasJobberAuth && !hasEagleViewAuth;
  const isFullyAuthenticated = isAuthenticated && hasJobberAuth;

  /**
   * Check auth status - always returns fresh data
   */
  const checkAuthStatus =
    useCallback(async (): Promise<AuthStatusResponse | null> => {
      // Prevent concurrent checks
      if (isCheckingRef.current) {
        console.log("[AuthContext] Check already in progress, waiting...");
        // Wait for current check to complete
        await new Promise((resolve) => setTimeout(resolve, 100));
        return authStatus;
      }

      isCheckingRef.current = true;

      try {
        console.log("[AuthContext] Checking auth status...");
        const status = await UnifiedAuthService.getAuthStatus(true); // Force fresh check

        if (status) {
          setAuthStatus(status);
          setLastCheckTime(Date.now());

          // Update org info from status
          if (status.organization) {
            setOrg(status.organization);
          }

          // Try to get additional info from cookies
          const savedOrgName = UnifiedAuthService.getOrgName();
          const savedJobberAccountId = UnifiedAuthService.getJobberAccountId();
          if (savedOrgName) setOrgName(savedOrgName);
          if (savedJobberAccountId) setJobberAccountId(savedJobberAccountId);

          // Update Jobber account info if available
          if (status.jobber?.account_id) {
            setJobberAccountId(status.jobber.account_id);
          }

          console.log("[AuthContext] Auth status updated:", {
            all_valid: status.all_valid,
            needs_reauth: status.needs_reauth,
          });
        } else {
          // Clear state on null response (likely 401)
          setAuthStatus(null);
          setOrg(null);
          setOrgName(null);
          setJobberAccountId(null);

          // Define public paths that don't require auth or are part of auth flow
          const publicPaths = [
            "/",
            "/jobber/auth",
            "/jobber/oauth-success",
            "/eagleview/auth",
            "/eagleview/integration-success",
            "/integrations",
          ];

          // Only redirect if not on a public path
          if (!publicPaths.includes(router.pathname)) {
            const returnTo = router.pathname;
            console.log(
              "[AuthContext] No auth found (401), redirecting to login. Return to:",
              returnTo,
            );
            router.push(
              `/jobber/auth?return_to=${encodeURIComponent(returnTo)}`,
            );
          }
        }

        return status;
      } catch (error) {
        console.error("[AuthContext] Error checking auth status:", error);
        return null;
      } finally {
        isCheckingRef.current = false;
        setIsLoading(false);
      }
    }, [authStatus, router]);

  /**
   * Get auth status - uses cache if recent, otherwise fetches fresh
   */
  const getAuthStatus =
    useCallback(async (): Promise<AuthStatusResponse | null> => {
      const cacheAge = Date.now() - lastCheckTime;

      // If we have cached data and it's recent, use it
      if (authStatus && cacheAge < CACHE_DURATION) {
        console.log("[AuthContext] Using cached auth status");
        return authStatus;
      }

      // Otherwise get fresh status
      return checkAuthStatus();
    }, [authStatus, lastCheckTime, checkAuthStatus]);

  /**
   * Ensure valid auth - always checks fresh for critical operations
   */
  const ensureValidAuth = useCallback(async (): Promise<boolean> => {
    const status = await checkAuthStatus();

    if (!status || !status.all_valid) {
      // Handle redirect if needed
      if (status?.needs_reauth && status.needs_reauth.length > 0) {
        const redirectUrl = UnifiedAuthService.getRedirectUrl(
          status.needs_reauth,
          router.asPath,
        );

        if (redirectUrl !== router.asPath) {
          console.log("[AuthContext] Redirecting for auth:", redirectUrl);
          router.push(redirectUrl);
        }
      }
      return false;
    }

    return true;
  }, [checkAuthStatus, router]);

  /**
   * Initial auth check on mount
   */
  useEffect(() => {
    const initAuth = async () => {
      // Check for existing token state
      const savedOrg = UnifiedAuthService.getOrg();
      const savedOrgName = UnifiedAuthService.getOrgName();
      const savedJobberAccountId = UnifiedAuthService.getJobberAccountId();

      if (savedOrg) setOrg(savedOrg);
      if (savedOrgName) setOrgName(savedOrgName);
      if (savedJobberAccountId) setJobberAccountId(savedJobberAccountId);

      // Check auth status
      await checkAuthStatus();
    };

    initAuth();
  }, []);

  /**
   * Set up periodic auth status checks
   */
  useEffect(() => {
    // Clear any existing interval
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
    }

    // Only poll if authenticated
    if (isAuthenticated) {
      console.log("[AuthContext] Starting auth status polling");

      checkIntervalRef.current = setInterval(async () => {
        console.log("[AuthContext] Periodic auth check");
        await checkAuthStatus();
      }, POLL_INTERVAL);
    }

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [isAuthenticated, checkAuthStatus]);

  const login = useCallback(
    (org: string) => {
      const returnTo = router.asPath;
      const authUrl = AuthService.getJobberAuthUrl(org, returnTo);
      window.location.href = authUrl;
    },
    [router],
  );

  const logout = useCallback(async () => {
    try {
      await AuthService.logout();
      setAuthStatus(null);
      setOrg(null);
      setOrgName(null);
      setJobberAccountId(null);
      router.push("/");
    } catch (error) {
      console.error("[AuthContext] Logout error:", error);
    }
  }, [router]);

  const handleOAuthCallback = useCallback(
    async (token: string, org: string) => {
      try {
        console.log("[AuthContext] Handling OAuth callback");
        const result = await AuthService.exchangeForSessionToken(token, org);

        if (result.success) {
          setOrg(result.org || org);
          setOrgName(result.orgName || null);
          setJobberAccountId(result.jobberAccountId || null);

          // Check auth status after token exchange
          await checkAuthStatus();
        } else {
          throw new Error(result.error || "Token exchange failed");
        }
      } catch (error) {
        console.error("[AuthContext] OAuth callback error:", error);
        throw error;
      }
    },
    [checkAuthStatus],
  );

  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const refreshed = await UnifiedAuthService.refreshAuth();
      if (refreshed) {
        await checkAuthStatus();
      }
      return refreshed;
    } catch (error) {
      console.error("[AuthContext] Token refresh error:", error);
      return false;
    }
  }, [checkAuthStatus]);

  // Backward compatibility methods
  const makeApiRequest = useCallback(
    async <T = any>(
      endpoint: string,
      options?: RequestInit,
    ): Promise<{ data?: T; error?: string }> => {
      return UnifiedAuthService.makeAuthenticatedRequest<T>(endpoint, options);
    },
    [],
  );

  const makeInternalRequest = useCallback(
    async <T = any>(
      endpoint: string,
      options?: RequestInit,
    ): Promise<{ data?: T; error?: string }> => {
      return UnifiedAuthService.makeAuthenticatedRequest<T>(endpoint, options);
    },
    [],
  );

  const fetchJobberAccountInfo = useCallback(async () => {
    // This is now handled by the auth status check
    await checkAuthStatus();
  }, [checkAuthStatus]);

  const value: AuthContextType = {
    authStatus,
    isAuthenticated,
    isLoading,
    org,
    orgName,
    jobberAccountId,
    hasJobberAuth,
    jobberAuthExpiresAt,
    needsJobberAuth,
    hasEagleViewAuth,
    eagleViewAuthMode,
    needsEagleViewAuth,
    isFullyAuthenticated,
    login,
    logout,
    handleOAuthCallback,
    refreshToken,
    checkAuthStatus,
    getAuthStatus,
    ensureValidAuth,
    makeApiRequest,
    makeInternalRequest,
    fetchJobberAccountInfo,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
